import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [team, setTeam] = useState('verwaltung');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success(t('auth.loginSuccess'));
      } else {
        await register(email, password, name, team);
        toast.success(t('auth.registerSuccess'));
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/5256688/pexels-photo-5256688.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
        }}
      >
        <div className="absolute inset-0 bg-white/20 backdrop-blur-sm" />
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="mb-8 text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_asunya-staff/artifacts/ih91cek0_IMG_6472.png" 
              alt="asunya Logo" 
              className="h-24 w-auto mx-auto mb-4"
            />
            <p className="text-zinc-600">{t('auth.subtitle')}</p>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-zinc-200">
            <div className="flex mb-6 bg-zinc-100 rounded-xl p-1">
              <button
                type="button"
                onClick={() => setIsLogin(true)}
                data-testid="login-tab-button"
                className={`flex-1 py-2 rounded-lg transition-all ${
                  isLogin ? 'bg-white shadow-sm' : 'text-zinc-600'
                }`}
              >
                {t('auth.login')}
              </button>
              <button
                type="button"
                onClick={() => setIsLogin(false)}
                data-testid="register-tab-button"
                className={`flex-1 py-2 rounded-lg transition-all ${
                  !isLogin ? 'bg-white shadow-sm' : 'text-zinc-600'
                }`}
              >
                {t('auth.register')}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="name">{t('auth.name')}</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
                    <Input
                      id="name"
                      type="text"
                      placeholder={t('auth.name')}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="name-input"
                      className="pl-10"
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="email">{t('auth.email')}</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@asunya.org"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="email-input"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">{t('auth.password')}</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="password-input"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              {!isLogin && (
                <div>
                  <Label htmlFor="team">{t('auth.team')}</Label>
                  <Select value={team} onValueChange={setTeam}>
                    <SelectTrigger data-testid="team-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="marketing">{t('teams.marketing')}</SelectItem>
                      <SelectItem value="website">{t('teams.website')}</SelectItem>
                      <SelectItem value="verwaltung">{t('teams.verwaltung')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="submit-button"
                className="w-full bg-[#D93025] hover:bg-[#B91C1C] text-white py-6 rounded-full text-lg font-medium shadow-lg"
              >
                {loading ? t('common.loading') : isLogin ? t('auth.login') : t('auth.register')}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
